/**
 * Shared TypeScript types for the Firm Contacts directory UI.
 *
 * Mirrors the validators in `convex/contacts/queries.ts` (firm contact
 * outputs). Re-exports the firm-category metadata that is also used by
 * the job CRM contact panel for visual consistency.
 */

import { Tag, type LucideIcon } from "lucide-react";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import type {
  ContactAddress,
  ContactType,
  ContactStatus,
  FirmContactCategory,
} from "@/components/jobs/phases/contacts/types";
import { FIRM_CATEGORY_META as BUILTIN_CATEGORY_META } from "@/components/jobs/phases/contacts/types";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import type { BadgeColor } from "@/ui/primitives/badge";

export {
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  formatAbn,
  formatAcn,
} from "@/components/jobs/phases/contacts/types";

export type { ContactType, ContactStatus, FirmContactCategory, ContactAddress };

/**
 * Categories a user can create or import a firm contact as. `org_member` rows
 * are auto-created from `orgMemberships` (they carry a `linkedUserId`), so
 * they are never an option in a form.
 */
export const CREATABLE_FIRM_CATEGORIES: ReadonlyArray<FirmContactCategory> = [
  "bank",
  "regulator",
  "professional",
  "referrer",
  "other",
];

/** An org-defined custom category (MOR-914), as returned by
 * `api.contacts.customCategories.listFirmContactCategories`. */
export interface FirmContactCustomCategory {
  _id: Id<"firmContactCategories">;
  name: string;
  color: TaskColor;
  icon?: WorkflowRoleIcon;
  description?: string;
  sortOrder: number;
}

/** Resolved display metadata for a contact's effective category. */
export interface FirmCategoryDisplay {
  /** Stable grouping/filter key: built-in category or `custom:<id>`. */
  key: string;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  badgeColor: BadgeColor;
}

/** Grouping/filter key for a custom category. */
export function customCategoryKey(id: Id<"firmContactCategories">): string {
  return `custom:${id}`;
}

/**
 * Resolve the category a contact should display (MOR-914): its custom
 * category when set and still active, otherwise its built-in category.
 * `customById` comes from `listFirmContactCategories` — contacts pointing
 * at an archived category silently fall back to the built-in one.
 */
export function resolveFirmCategoryDisplay(
  contact: Pick<FirmContactRow, "category" | "customCategoryId">,
  customById: ReadonlyMap<string, FirmContactCustomCategory>,
): FirmCategoryDisplay {
  const custom = contact.customCategoryId
    ? customById.get(contact.customCategoryId)
    : undefined;
  if (custom) {
    return {
      key: customCategoryKey(custom._id),
      label: custom.name,
      shortLabel: custom.name,
      icon: roleIconComponent(custom.icon) ?? Tag,
      badgeColor: custom.color,
    };
  }
  const meta = BUILTIN_CATEGORY_META[contact.category];
  return {
    key: contact.category,
    label: meta.label,
    shortLabel: meta.shortLabel,
    icon: meta.icon,
    badgeColor: meta.badgeColor,
  };
}

export interface FirmContactRow {
  _id: Id<"firmContacts">;
  _creationTime: number;
  orgId: Id<"organizations">;
  displayName: string;
  contactType: ContactType;
  category: FirmContactCategory;
  customCategoryId?: Id<"firmContactCategories">;
  abn?: string;
  acn?: string;
  email?: string;
  phone?: string;
  website?: string;
  addresses: Array<ContactAddress>;
  notes?: string;
  linkedUserId?: Id<"users">;
  /** MOR-995 — parent firm this contact nests under (person → organisation). */
  parentFirmContactId?: Id<"firmContacts">;
  parentDisplayName?: string;
  status: ContactStatus;
  createdBy: Id<"users">;
  lastEditedBy?: Id<"users">;
  linkedUser?: {
    _id: Id<"users">;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  jobsUsing: number;
}

export interface FirmContactWithHistory extends FirmContactRow {
  changeHistory: Doc<"firmContacts">["changeHistory"];
}

/**
 * Derived address state for a firm contact (MOR-913) — the first non-empty
 * `state` across its addresses, upper-cased so "nsw"/"NSW" bucket together
 * in the table's filter/grouping. `null` when no address carries a state.
 */
export function firmContactState(
  contact: Pick<FirmContactRow, "addresses">,
): string | null {
  for (const address of contact.addresses) {
    const state = address.state?.trim();
    if (state) return state.toUpperCase();
  }
  return null;
}
